// src/modules/operaciones/inventarios/InventariosTraslados.js
//
// Módulo de Traslados Pendientes: lista los traslados entre bodegas que aún no están confirmados ni anulados.
// • Filtros: búsqueda global, rango de fechas, Origen y Destino.
// • Exportación: genera un archivo Excel (.xls) con formato de tabla idéntico al corporativo (título combinado, verde y bordes).
// • Paginación y ordenamiento en el cliente.

import React, { useState, useEffect, useMemo } from 'react';
import { TEMAS_PLANOS, useTemaOscuro } from '../../../utils/tema';
import { aISO, haceDias } from '../../../utils/format';
import { fechaDe } from './utils/trasladosComun';
import { exportarTrasladosAExcel } from './utils/exportarTraslados';
import TablaTraslados from './components/TablaTraslados';

const API_URL = `${process.env.REACT_APP_API_URL}/api/invent`;

// Ventana de hasta 7 números de página alrededor de la actual
const ventanaPaginas = (actual, total) => {
  const ancho = 7;
  let ini = Math.max(1, actual - Math.floor(ancho / 2));
  const fin = Math.min(total, ini + ancho - 1);
  ini = Math.max(1, fin - ancho + 1);
  return Array.from({ length: fin - ini + 1 }, (_, i) => ini + i);
};

/* =========================================================================
   COMPONENTE PRINCIPAL
   ========================================================================= */

export default function InventariosTraslados({ onBackToMenu }) {
  const [traslados, setTraslados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recarga, setRecarga] = useState(0);
  
  // Estados de filtros
  const [busqueda, setBusqueda] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [filtroOrigen, setFiltroOrigen] = useState('');
  const [filtroDestino, setFiltroDestino] = useState('');

  // Paginación y orden
  const [paginaActual, setPaginaActual] = useState(1);
  const [itemsPorPagina, setItemsPorPagina] = useState(10);
  const [orden, setOrden] = useState({ campo: null, dir: 'asc' });
  
  const [temaOscuro, setTemaOscuro] = useTemaOscuro();

  // Carga de datos
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetch(`${API_URL}/traslados/pendientes`, { signal: controller.signal })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
        if (!controller.signal.aborted) setTraslados(Array.isArray(data.data) ? data.data : []);
      })
      .catch((err) => {
        if (controller.signal.aborted || err.name === 'AbortError') return;
        setTraslados([]);
        setError(err.message || 'Error al cargar traslados');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });

    return () => controller.abort();
  }, [recarga]);

  const rangoInvalido = Boolean(fechaDesde && fechaHasta && fechaDesde > fechaHasta);
  const hayFiltros = Boolean(busqueda || fechaDesde || fechaHasta || filtroOrigen || filtroDestino);

  const fijarFechas = (desde, hasta) => { setFechaDesde(desde); setFechaHasta(hasta); setPaginaActual(1); };
  
  const limpiarFiltros = () => { 
    setBusqueda(''); 
    setFiltroOrigen('');
    setFiltroDestino('');
    fijarFechas('', ''); 
  };

  const alternarOrden = (campo) => {
    setOrden((prev) => {
      if (prev.campo !== campo) return { campo, dir: 'asc' };
      if (prev.dir === 'asc') return { campo, dir: 'desc' };
      return { campo: null, dir: 'asc' };
    });
    setPaginaActual(1);
  };

  // Extraer bodegas únicas de los datos cargados
  const bodegasUnicas = useMemo(() => {
    const set = new Set();
    traslados.forEach(t => {
      if (t.ORIGEN) set.add(t.ORIGEN);
      if (t.DESTINO) set.add(t.DESTINO);
    });
    return Array.from(set).sort();
  }, [traslados]);

  // Filtrado de datos
  const trasladosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return traslados.filter((t) => {
      const f = fechaDe(t);
      
      const cumpleBusqueda = (!q || [t.PREFIJO, t.NUMERO, t.CONCEPTO, t.REFERENCIA, t.ORIGEN, t.DESTINO].some((v) => String(v ?? '').toLowerCase().includes(q)));
      const cumpleDesde = (!fechaDesde || (f !== '' && f >= fechaDesde));
      const cumpleHasta = (!fechaHasta || (f !== '' && f <= fechaHasta));
      const cumpleOrigen = (!filtroOrigen || String(t.ORIGEN) === filtroOrigen);
      const cumpleDestino = (!filtroDestino || String(t.DESTINO) === filtroDestino);

      return cumpleBusqueda && cumpleDesde && cumpleHasta && cumpleOrigen && cumpleDestino;
    });
  }, [traslados, busqueda, fechaDesde, fechaHasta, filtroOrigen, filtroDestino]);

  // Ordenamiento
  const trasladosOrdenados = useMemo(() => {
    if (!orden.campo) return trasladosFiltrados;
    const f = orden.dir === 'asc' ? 1 : -1;
    const valor = (t) => (orden.campo === 'FECHA' ? fechaDe(t) : String(t[orden.campo] ?? ''));
    return [...trasladosFiltrados].sort((a, b) => valor(a).localeCompare(valor(b), 'es', { numeric: true, sensitivity: 'base' }) * f);
  }, [trasladosFiltrados, orden]);

  // Paginación
  const totalPaginas = Math.max(1, Math.ceil(trasladosOrdenados.length / itemsPorPagina));
  const paginaSegura = Math.min(paginaActual, totalPaginas);
  const indiceInicial = (paginaSegura - 1) * itemsPorPagina;
  const trasladosPaginados = trasladosOrdenados.slice(indiceInicial, indiceInicial + itemsPorPagina);
  const desde = trasladosOrdenados.length === 0 ? 0 : indiceInicial + 1;
  const hasta = Math.min(indiceInicial + itemsPorPagina, trasladosOrdenados.length);

  // Estilos UI
  const tema = temaOscuro ? TEMAS_PLANOS.oscuro : TEMAS_PLANOS.claro;
  const hoy = aISO(new Date());
  const ayer = aISO(haceDias(1));
  
  const botonPag = `h-8 flex items-center justify-center rounded-lg ${tema.bgBtn} ${tema.textMain} border ${tema.borderInput} disabled:opacity-50 hover:opacity-80 transition-colors shadow-sm cursor-pointer`;
  const claseInputFecha = `${tema.bgInput} border ${tema.borderInput} ${tema.textInput} text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 cursor-pointer shadow-sm`;
  const claseSelect = `${tema.bgInput} border ${tema.borderInput} ${tema.textInput} text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 cursor-pointer shadow-sm`;
  
  const atajos = [['Hoy', hoy], ['Ayer', ayer]];
  const camposFecha = [
    { etiqueta: 'Desde', valor: fechaDesde, cambiar: (v) => fijarFechas(v, fechaHasta), limite: { max: fechaHasta || undefined } },
    { etiqueta: 'Hasta', valor: fechaHasta, cambiar: (v) => fijarFechas(fechaDesde, v), limite: { min: fechaDesde || undefined } },
  ];

  return (
    <div className={`min-h-screen ${tema.bgApp} flex flex-col p-4 sm:p-8 font-sans transition-colors duration-300`}>

      {/* HEADER */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center text-2xl shadow-lg shadow-blue-600/30">
            🚚
          </div>
          <div>
            <h1 className={`text-2xl font-extrabold ${tema.textMain} tracking-tight`}>
              Traslados <span className="text-blue-500">Pendientes</span>
            </h1>
            <p className={`${tema.textSub} text-sm mt-0.5`} aria-live="polite">
              {loading ? 'Cargando traslados…' : `Mostrando ${trasladosOrdenados.length} traslados`}
            </p>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => exportarTrasladosAExcel(trasladosOrdenados)}
            disabled={loading || trasladosOrdenados.length === 0}
            className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            title="Exportar la lista filtrada a Excel"
          >
            📊 Exportar Excel
          </button>

          <button
            onClick={() => setTemaOscuro(!temaOscuro)}
            className={`p-2.5 rounded-xl ${tema.bgBtn} border ${tema.borderInput} ${tema.textMain} hover:opacity-80 transition-all shadow-sm cursor-pointer`}
            title={temaOscuro ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
          >
            {temaOscuro ? '☀️' : '🌙'}
          </button>
          <button
            onClick={onBackToMenu}
            className={`px-4 py-2.5 rounded-xl ${tema.bgBtn} ${tema.textMain} text-sm font-bold border ${tema.borderInput} hover:opacity-80 transition-all cursor-pointer flex items-center gap-2 shadow-sm`}
          >
            ← Volver al Menú
          </button>
        </div>
      </div>

      {/* FILTROS */}
      <div className="flex flex-col xl:flex-row gap-4 mb-6">
        
        <div className="relative flex-1">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">🔍</span>
          <input
            type="text"
            placeholder="Buscar por código, concepto, origen o destino..."
            aria-label="Buscar traslados"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setPaginaActual(1); }}
            className={`w-full ${tema.bgInput} border ${tema.borderInput} ${tema.textInput} text-sm rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:border-blue-500 transition-colors placeholder:text-gray-500 shadow-sm`}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          
          {/* Selector Origen */}
          <label className={`flex items-center gap-1.5 text-xs ${tema.textSub}`}>
            Origen:
            <select 
              value={filtroOrigen} 
              onChange={(e) => { setFiltroOrigen(e.target.value); setPaginaActual(1); }}
              className={claseSelect}
            >
              <option value="">Todas</option>
              {bodegasUnicas.map(b => <option key={b} value={b}>Bodega {b}</option>)}
            </select>
          </label>

          {/* Selector Destino */}
          <label className={`flex items-center gap-1.5 text-xs ${tema.textSub}`}>
            Destino:
            <select 
              value={filtroDestino} 
              onChange={(e) => { setFiltroDestino(e.target.value); setPaginaActual(1); }}
              className={claseSelect}
            >
              <option value="">Todas</option>
              {bodegasUnicas.map(b => <option key={b} value={b}>Bodega {b}</option>)}
            </select>
          </label>

          <span className={`text-xs font-bold ${tema.textSub} uppercase tracking-wider ml-2 hidden sm:block`}>Fecha:</span>

          {atajos.map(([etiqueta, dia]) => (
            <button
              key={etiqueta}
              onClick={() => fijarFechas(dia, dia)}
              className={`px-3 py-2 rounded-lg text-xs font-bold transition-colors cursor-pointer ${fechaDesde === dia && fechaHasta === dia ? 'bg-blue-600 text-white border border-blue-600' : `${tema.bgBtn} ${tema.textMain} border ${tema.borderInput} hover:opacity-80`}`}
            >
              {etiqueta}
            </button>
          ))}

          {camposFecha.map((c) => (
            <label key={c.etiqueta} className={`flex items-center gap-1.5 text-xs ${tema.textSub}`}>
              {c.etiqueta}
              <input type="date" value={c.valor} {...c.limite} onChange={(e) => c.cambiar(e.target.value)} className={claseInputFecha} />
            </label>
          ))}

          {(fechaDesde || fechaHasta || filtroOrigen || filtroDestino) && (
            <button
              onClick={() => { fijarFechas('', ''); setFiltroOrigen(''); setFiltroDestino(''); }}
              className="px-3 py-2 rounded-lg text-xs font-bold transition-colors bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20 cursor-pointer"
              title="Limpiar filtros"
            >
              ✕ Limpiar
            </button>
          )}
        </div>
      </div>

      {rangoInvalido && (
        <div role="status" className="mb-4 px-4 py-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-600 text-sm font-bold">
          La fecha «Desde» es posterior a «Hasta»: ajuste el rango.
        </div>
      )}

      {error && (
        <div role="alert" aria-live="assertive" className="mb-4 px-4 py-3 rounded-xl border border-red-500/40 bg-red-500/10 text-red-500 text-sm font-bold flex items-center justify-between gap-3">
          <span>Error: {error}</span>
          <button onClick={() => setRecarga((n) => n + 1)} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-red-500/40 hover:bg-red-500/10 cursor-pointer">
            Reintentar
          </button>
        </div>
      )}

      {/* TABLA */}
      <TablaTraslados
        tema={tema}
        loading={loading}
        orden={orden}
        alternarOrden={alternarOrden}
        trasladosPaginados={trasladosPaginados}
        hayFiltros={hayFiltros}
        limpiarFiltros={limpiarFiltros}
        indiceInicial={indiceInicial}
      />

      {/* PAGINACIÓN */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
        <div className="flex items-center gap-3">
          <select
            value={itemsPorPagina}
            onChange={(e) => { setItemsPorPagina(Number(e.target.value)); setPaginaActual(1); }}
            aria-label="Registros por página"
            className={`${tema.bgBtn} border ${tema.borderInput} ${tema.textMain} text-xs rounded-lg px-3 py-2 focus:outline-none cursor-pointer shadow-sm`}
          >
            {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <p className={`${tema.textSub} text-xs`} aria-live="polite">
            Mostrando {desde} - {hasta} de {trasladosOrdenados.length} registros
          </p>
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => setPaginaActual(Math.max(1, paginaSegura - 1))} disabled={paginaSegura === 1} className={`w-8 ${botonPag}`} aria-label="Página anterior">
            ‹
          </button>

          {ventanaPaginas(paginaSegura, totalPaginas).map((pag) => (
            <button
              key={pag}
              onClick={() => setPaginaActual(pag)}
              aria-current={paginaSegura === pag ? 'page' : undefined}
              className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold transition-colors border cursor-pointer ${
                paginaSegura === pag
                  ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/30'
                  : `${tema.bgBtn} ${tema.textMain} ${tema.borderInput} hover:opacity-80`
              }`}
            >
              {pag}
            </button>
          ))}

          <button onClick={() => setPaginaActual(Math.min(totalPaginas, paginaSegura + 1))} disabled={paginaSegura === totalPaginas} className={`w-8 ${botonPag}`} aria-label="Página siguiente">
            ›
          </button>
        </div>
      </div>

    </div>
  );
}